import { FontAwesome } from "@expo/vector-icons";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
} from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import * as React from "react";
import { View, Text, TouchableOpacity, ActivityIndicator } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { onAuthFailure } from "../utils/authEvents";
import Colors from "../constants/Colors";
import useColorScheme from "../hooks/useColorScheme";
import QRCodeModalScreen from "../screens/QRCodeModalScreen";
import PublicTableScreen from "../screens/PublicTableScreen";
import PublicComputerScreen from "../screens/PublicComputerScreen";
import TableDetailsScreen from "../screens/TableDetailsScreen";
import DeliveryDetailsScreen from "../screens/DeliveryDetailsScreen";
import NotFoundScreen from "../screens/NotFoundScreen";
import MenuScreen from "../screens/MenuScreen";
import VRScreen from "../screens/VRScreen";
import MoreScreen from "../screens/MoreScreen";
import EsportsScreen from "../screens/EsportsScreen";
import TablesScreen from "../screens/TablesScreen";
import OrdersScreen from "../screens/OrdersScreen";
import DeliveriesScreen from "../screens/DeliveriesScreen";
import OperatorSettingsScreen from "../screens/OperatorSettingsScreen";
import OperatorSettingsLightsAndColorsScreen from '../screens/OperatorSettingsLightsAndColorsScreen';
import OperatorSettingsSummaryScreen from '../screens/OperatorSettingsSummaryScreen';
import OperatorSettingsReportScreen from '../screens/OperatorSettingsReportScreen';
import OperatorSettingsInviteScreen from '../screens/OperatorSettingsInviteScreen';
import OperatorSettingsProfileScreen from '../screens/OperatorSettingsProfileScreen';
import OperatorSettingsReceiptsScreen from "../screens/OperatorSettingsReceipts";
import OperatorBenefitsScreen from "../screens/OperatorBenefitsScreen";
import Config from "../constants/Config";
import OperatorComputersScreen from "../screens/OperatorComputersScreen";
import OperatorVRScreen from "../screens/OperatorVRScreen";
import PublicInviteScreen from "../screens/PublicInviteScreen";
import LinkingConfiguration from "./LinkingConfiguration";
import Logo1 from "../components/Logo1";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Octicons } from "@expo/vector-icons";
import { MaterialIcons } from "@expo/vector-icons";
import OperatorGamingDetailsScreen from "../screens/OperatorGamingDetailsScreen";
import InvoicesScreen from "../screens/InvoicesScreen";
import InvoicePreviewScreen from "../screens/InvoicePreviewScreen";
import SuppliersScreen from "../screens/SuppliersScreen";
import InventoryScreen from "../screens/InventoryScreen";
import InventoryCheckpointsScreen from "../screens/InventoryCheckpointsScreen";
import OperatorCategoriesScreen from "../screens/OperatorCategoriesScreen";
import OperatorProductsScreen from "../screens/OperatorProductsScreen";
import DesignerScreen from "../screens/DesignerScreen";
import OperatorSettingsOperatorsScreen from "../screens/OperatorSettingsOperatorsScreen";
import SalesChartsScreen from "../screens/SalesChartsScreen";
import OperatorSettingsPasskeysScreen from "../screens/OperatorSettingsPasskeysScreen";
import CashRegisterScreen from "../screens/CashRegisterScreen";
import KioskOrderScreen from "../screens/KioskOrderScreen";
import LandingScreen from "../screens/LandingScreen";
import LegalTermsScreen from "../screens/LegalTermsScreen";
import LegalRefundScreen from "../screens/LegalRefundScreen";
import LegalDeliveryScreen from "../screens/LegalDeliveryScreen";
import OnlineOrderScreen from "../screens/OnlineOrderScreen";
import OperatorSettingsKioskDevicesScreen from "../screens/OperatorSettingsKioskDevicesScreen";
import OperatorSettingsThemeDesignerScreen from "../screens/OperatorSettingsThemeDesignerScreen";
import i18n from "../utils/localization";
import { getCurrentAppTheme } from "../utils/themeDesigner";

export default function Navigation({
  colorScheme,
  themeVersion,
}) {
  const navigationTheme = React.useMemo(() => {
    const baseTheme = colorScheme === "dark" ? DarkTheme : DefaultTheme;
    const appTheme = getCurrentAppTheme();
    if (!appTheme) {
      return baseTheme;
    }

    const isDark = colorScheme === "dark";
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        primary: appTheme.primary,
        background: isDark ? appTheme.backgroundDark : appTheme.backgroundLight,
        card: isDark ? appTheme.surfaceDark : appTheme.surfaceLight,
        text: isDark ? appTheme.textDark : appTheme.textLight,
        border: appTheme.accent,
        notification: appTheme.secondary,
      },
    };
  }, [colorScheme, themeVersion]);

  return (
    <NavigationContainer
      linking={LinkingConfiguration}
      theme={navigationTheme}
    >
      <RootNavigator />
    </NavigationContainer>
  );
}

const Stack = createNativeStackNavigator();

function RootNavigator() {
  return (
    <Stack.Navigator initialRouteName="Root">
      <Stack.Screen
        name="KioskOrder"
        component={KioskOrderScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="Root"
        component={BottomTabNavigator}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="PublicInvite"
        title="Create your account"
        component={PublicInviteScreen}
        options={{ headerShown: false, title: "Create your account" }}
      />
      <Stack.Screen
        name="PublicTableCode"
        component={PublicTableScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="OnlineOrderStatus"
        component={OnlineOrderScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="OperatorRoot"
        component={OperatorBottomTabNavigator}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="LegacyOperatorRoot"
        component={OperatorBottomTabNavigator}
        options={{ headerShown: false }}
      />
      
      {Config.modules.delivery && <Stack.Screen
        name="DeliveriesRoot"
        component={DeliveriesRoot}
        options={{ headerShown: false }}
      /> }

      <Stack.Screen
        name="OrdersRoot"
        component={OrdersScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="LegalTerms"
        component={LegalTermsScreen}
        options={{
          headerShown: true,
          title: "Terms & conditions",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
        }}
      />
      <Stack.Screen
        name="LegalRefund"
        component={LegalRefundScreen}
        options={{
          headerShown: true,
          title: "Refund policy",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
        }}
      />
      <Stack.Screen
        name="LegalDelivery"
        component={LegalDeliveryScreen}
        options={{
          headerShown: true,
          title: "Delivery terms",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
        }}
      />
      <Stack.Screen
        name="NotFound"
        component={NotFoundScreen}
        options={{
          title: "Oops!",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
        }}
      />
      <Stack.Group screenOptions={{ presentation: "modal" }}>
        <Stack.Screen
          name="QRCodeModal"
          component={QRCodeModalScreen}
          options={{
            title: "Harlem Table QR Code",
            headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          }}
        />
        <Stack.Screen
          name="QRCodeModalLegacy"
          component={QRCodeModalScreen}
          options={{
            title: "Harlem Table QR Code",
            headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          }}
        />
        <Stack.Screen
          name="QRCodeModalPC"
          component={QRCodeModalScreen}
          options={{
            title: "Harlem Gaming QR Code",
            headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          }}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
}

function TablesRoot() {
  return (
    <Stack.Navigator path="">
      <Stack.Screen
        name="Tables"
        path=""
        component={TablesScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="TableDetails"
        component={TableDetailsScreen}
        options={{ headerShown: true, title: 'Table details',
        headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily }, }}
      />
    </Stack.Navigator>
  );
}


function DeliveriesRoot() {
  return (
    <Stack.Navigator path="">
      <Stack.Screen
        name="Deliveries"
        path=""
        component={DeliveriesScreen}
        options={{ headerShown: true, title: i18n.t('Deliveries') }}
      />
      <Stack.Screen
        name="DeliveryDetails"
        component={DeliveryDetailsScreen}
        options={{ headerShown: true, title: 'Delivery details',
        headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily }, }}
      />
    </Stack.Navigator>
  );
}


function OperatorSettingsRoot() {
  // Custom back button that navigates to OperatorSettings instead of goBack()
  // This fixes deep linking directly to inner screens
  const settingsBackButton = (navigation) => ({
    headerLeft: () => (
      <TouchableOpacity
        onPress={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
          } else {
            navigation.navigate("OperatorSettings");
          }
        }}
        style={{ marginLeft: 8, padding: 8 }}
      >
        <MaterialIcons name="arrow-back" size={24} color={Config.companyColor || '#007AFF'} />
      </TouchableOpacity>
    ),
  });

  return (
    <Stack.Navigator path="">
      <Stack.Screen
        name="OperatorSettings"
        path=""
        component={OperatorSettingsScreen}
        options={{ headerShown: false, title: 'Settings' }}
      />
      <Stack.Screen
        name="OperatorSettingsLightsAndColors"
        component={OperatorSettingsLightsAndColorsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Lights & colors',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsReceipts"
        component={OperatorSettingsReceiptsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Receipts',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsProfile"
        component={OperatorSettingsProfileScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Profile',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsSummary"
        component={OperatorSettingsSummaryScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Summary',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsReport"
        component={OperatorSettingsReportScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Report',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsInvite"
        component={OperatorSettingsInviteScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Invite',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsInvoices"
        component={InvoicesScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Invoices',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="InvoicePreview"
        component={InvoicePreviewScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Invoice Preview',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="Suppliers"
        component={SuppliersScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Suppliers',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="Inventory"
        component={InventoryScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Inventory',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="InventoryCheckpoints"
        component={InventoryCheckpointsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Inventory Checkpoints',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorCategories"
        component={OperatorCategoriesScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Categories',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorProducts"
        component={OperatorProductsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Products',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="Designer"
        component={DesignerScreen}
        options={{ headerShown: false, title: 'Designer',
        headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily }, }}
      />
      <Stack.Screen
        name="OperatorSettingsOperators"
        component={OperatorSettingsOperatorsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Operators',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="SalesCharts"
        component={SalesChartsScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Sales Charts',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsPasskeys"
        component={OperatorSettingsPasskeysScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Passkeys',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="CashRegister"
        component={CashRegisterScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Cash Register',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsKioskDevices"
        component={OperatorSettingsKioskDevicesScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: 'Kiosk Devices',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
      <Stack.Screen
        name="OperatorSettingsThemeDesigner"
        component={OperatorSettingsThemeDesignerScreen}
        options={({ navigation }) => ({
          headerShown: true,
          title: "Theme Designer",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          ...settingsBackButton(navigation),
        })}
      />
    </Stack.Navigator>
  );
}

function GamingRoot() {
  return (
    <Stack.Navigator path="">
      <Stack.Screen
        name="Gaming"
        path=""
        component={OperatorComputersScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="GamingDetails"
        component={OperatorGamingDetailsScreen}
        options={{ headerShown: true, title: 'Gaming details' }}
      />
    </Stack.Navigator>
  );
}

function VRRoot() {
  return (
    <Stack.Navigator path="">
      <Stack.Screen
        name="VRGaming"
        path=""
        component={OperatorVRScreen}
        options={{ headerShown: false, title: 'VR gaming' }}
      />
      <Stack.Screen
        name="VRGamingDetails"
        component={OperatorGamingDetailsScreen}
        options={{ headerShown: true, title: 'VR details' }}
      />
    </Stack.Navigator>
  );
}

function MenuRoot() {
  return (
    <Stack.Navigator path="">
      <Stack.Screen
        name="Menu"
        component={MenuScreen}
        options={{
          title: Config.menuTitle,
          headerShown: false,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <Text
              style={{
                fontFamily: Config.theme.defaultFontFamily,
                textShadowColor: color,
                textShadowOutline: 1,
                textShadowRadius: 3,
                color: color
              }}
            >
              {Config.menuTitle}
            </Text>
          ),
          tabBarLabel: "",
          tabBarLabelStyle: {},
        }}
      />
      <Stack.Screen
        name="PublicTable"
        component={PublicTableScreen}
        options={{
          title: "Halrem table",
          headerShown: true,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <Text
              style={{
                fontFamily: Config.theme.defaultFontFamily,
                textShadowColor: colorScheme === "dark" ? "white" : "black",
                textShadowOutline: 1,
                textShadowRadius: 3,
                color,
              }}
            >
              {Config.menuTitle}
            </Text>
          ),
          tabBarLabel: "",
          tabBarLabelStyle: {},
        }}
      />
      
      <Stack.Screen
        name="PublicComputer"
        component={PublicComputerScreen}
        options={{
          title: "RFX gaming",
          headerShown: true,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <Text
              style={{
                fontFamily: Config.theme.defaultFontFamily,
                textShadowColor: colorScheme === "dark" ? "white" : "black",
                textShadowOutline: 1,
                textShadowRadius: 3,
                color,
              }}
            >
              Harlem
            </Text>
          ),
          tabBarLabel: "",
          tabBarLabelStyle: {},
        }}
      />
    </Stack.Navigator>
  );
}

const BottomTab = createBottomTabNavigator();

const OperatorBottomTab = createBottomTabNavigator();


function OperatorBottomTabNavigator({ route, navigation }) {
  const colorScheme = useColorScheme();
  const activeTintColor = Config.companyColor || Colors[colorScheme].tint;
  const [isAuthChecked, setIsAuthChecked] = React.useState(false);
  const [hasToken, setHasToken] = React.useState(false);

  // Check for token on mount and whenever the navigator regains focus
  const checkAuth = React.useCallback(async () => {
    try {
      const storedToken = await AsyncStorage.getItem("token");
      setHasToken(Boolean(storedToken));
    } catch (ex) {
      setHasToken(false);
    } finally {
      setIsAuthChecked(true);
    }
  }, []);

  React.useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Immediately redirect to login when an auth failure is detected
  React.useEffect(() => {
    return onAuthFailure(() => {
      setHasToken(false);
      navigation.navigate('OperatorSettingsRoot', { screen: 'OperatorSettings' });
    });
  }, [navigation]);

  // Show loading while checking auth
  if (!isAuthChecked) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Guard: redirect unauthenticated users to the login tab.
  // If the user has no token, pressing any tab other than Settings
  // will be intercepted and redirected to the Settings/login screen.
  const guardedTabPress = (navigation, targetScreen, targetParams) => ({
    tabPress: (e) => {
      // Prevent default so auth is checked before tab switch.
      e.preventDefault();
      // Re-check auth from storage on every tab press (catches logout / expiry)
      AsyncStorage.getItem("token").then((storedToken) => {
        setHasToken(Boolean(storedToken));
        if (!storedToken) {
          navigation.navigate('OperatorSettingsRoot', { screen: 'OperatorSettings' });
        } else if (targetScreen) {
          navigation.navigate(targetScreen, targetParams);
        }
      }).catch(() => {
        navigation.navigate('OperatorSettingsRoot', { screen: 'OperatorSettings' });
      });
    },
  });

  // If not logged in, start on the Settings tab (which shows the login form)
  const initialRoute = hasToken ? "TablesRoot" : "OperatorSettingsRoot";

  return (
    <OperatorBottomTab.Navigator
      initialRouteName={initialRoute}
      screenOptions={{
        tabBarActiveTintColor: activeTintColor,
        tabBarLabelStyle: {
          position: "absolute",
          textAlignVertical: "center",
        },
      }}
    >
      <OperatorBottomTab.Screen
        name="TablesRoot"
        component={TablesRoot}
        options={({ navigation }) => ({
          title: "Tables",
          headerShown: false,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons
              name="chart-scatter-plot"
              size={24}
              color={color}
            />
          ),
          tabBarLabel: "",
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        })}
        listeners={({ navigation }) =>
          guardedTabPress(navigation, 'TablesRoot', { screen: 'Tables' })
        }
      />

      {Config.modules.delivery && <OperatorBottomTab.Screen
        name="DeliveriesRoot"
        component={DeliveriesRoot}
        options={({ navigation }) => ({
          title: "Deliveries",
          headerShown: false,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <MaterialIcons
              name="electric-bike"
              size={24}
              color={color}
            />
          ),
          tabBarLabel: "",
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        })}
        listeners={({ navigation }) =>
          guardedTabPress(navigation, 'DeliveriesRoot', { screen: 'Deliveries' })
        }
      />}

      <OperatorBottomTab.Screen
        name="Orders"
        component={OrdersScreen}
        options={({ navigation }) => ({
          title: "Orders",
          headerShown: false,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <Octicons name="checklist" size={24} color={color} />
          ),
          tabBarLabel: "",
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        })}
        listeners={({ navigation }) =>
          guardedTabPress(navigation, "Orders", null)
        }
      />


      {Config.modules.esports && <OperatorBottomTab.Screen
        name="GamingRoot"
        component={GamingRoot}
        options={{
          title: "Gaming",
          headerShown: false,
          tabBarIcon: ({ color }) => (
            <MaterialIcons name="computer" size={24} color={color} />
          ),
          tabBarLabel: '',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        }}
        listeners={({ navigation }) =>
          guardedTabPress(navigation, 'GamingRoot', { screen: 'Gaming' })
        }
      />}

      {Config.modules.vr && <OperatorBottomTab.Screen
        name="VRRoot"
        component={VRRoot}
        options={{
          title: "VR",
          headerShown: false,
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons name="virtual-reality" size={24} color={color} />
          ),
          tabBarLabel: '',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        }}
        listeners={({ navigation }) =>
          guardedTabPress(navigation, 'VRRoot', { screen: 'VRGaming' })
        }
      />}


      {Config.modules.benefits && <OperatorBottomTab.Screen
        name="OperatorBenefits"
        component={OperatorBenefitsScreen}
        options={{
          title: "Benefits",
          tabBarIcon: ({ color }) => (
            <MaterialIcons name="emoji-food-beverage" size={24} color={color} />
          ),
          tabBarLabel: '',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        }}
        listeners={({ navigation }) =>
          guardedTabPress(navigation, "OperatorBenefits", null)
        }
      /> }

      <OperatorBottomTab.Screen
        name="OperatorSettingsRoot"
        component={OperatorSettingsRoot}
        options={{
          title: "Settings",
          tabBarIcon: ({ color }) => (
            <MaterialIcons name="settings" size={24} color={color} />
          ),
          tabBarLabel: '',
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        }}
        listeners={({ navigation }) => ({
          tabPress: (e) => {
            // Re-check auth when coming back to settings (user may have just logged in)
            checkAuth();
            navigation.navigate('OperatorSettingsRoot', { screen: 'OperatorSettings' });
          },
        })}
      />
    </OperatorBottomTab.Navigator>
  );
}

function BottomTabNavigator({ route }) {
  const colorScheme = useColorScheme();
  const activeTintColor = Config.companyColor || Colors[colorScheme].tint;

  return (
    <BottomTab.Navigator
      screenOptions={{
        tabBarActiveTintColor: activeTintColor,
        tabBarStyle: {},
        tabBarLabelStyle: {
          position: "absolute",
          textAlignVertical: "center",
        },
      }}
    >
      <BottomTab.Screen
        name="Landing"
        component={LandingScreen}
        options={{
          title: "Home",
          headerShown: false,
          tabBarIcon: ({ color }) => (
            <MaterialIcons name="home" size={24} color={color} />
          ),
          tabBarStyle: { display: "none" },
          tabBarLabel: "",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        }}
      />

      <BottomTab.Screen
        name="MenuRoot"
        component={MenuRoot}
        options={({ navigation }) => ({
          title: "Menu",
          headerShown: false,
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarIcon: ({ color }) => (
            <MaterialIcons name="menu-book" size={24} color={color} />
          ),
          tabBarLabel: "",
        })}
      />

      {Config.modules.onlineOrdering && <BottomTab.Screen
        name="OnlineOrder"
        component={OnlineOrderScreen}
        options={{
          title: "Order",
          headerShown: false,
          tabBarIcon: ({ color }) => (
            <MaterialIcons name="shopping-cart" size={24} color={color} />
          ),
          tabBarLabel: "",
          headerTitleStyle: { fontFamily: Config.theme.titleFontFamily || Config.theme.defaultFontFamily },
          tabBarLabelStyle: { fontFamily: Config.theme.defaultFontFamily },
        }}
      />}

      {Config.modules.vr && <BottomTab.Screen
        name="VR"
        component={VRScreen}
        options={{
          title: "HarlemVR",
          headerShown: false,
          tabBarIcon: ({ color }) => (
            <MaterialCommunityIcons name="virtual-reality" size={24} color={color} />
          ),
          tabBarLabel: "",
          tabBarLabelStyle: {
            textShadowColor: colorScheme === "dark" ? "white" : "black",
            textShadowOutline: 1,
            textShadowRadius: colorScheme === "dark" ? 3 : 1,
          },
        }}
      /> }

      {Config.modules.esports && 
      <BottomTab.Screen
        name="Esports"
        component={EsportsScreen}
        options={{
          title: "RFX e-sports",
          headerShown: false,
          tabBarIcon: ({ color }) => <Logo1 active={color===activeTintColor} />,
          tabBarLabel: "",
          tabBarLabelStyle: {
            textShadowColor: colorScheme === "dark" ? "white" : "black",
            textShadowOutline: 1,
            textShadowRadius: colorScheme === "dark" ? 3 : 1,
          },
        }}
      />}


      <BottomTab.Screen
        name="More"
        component={MoreScreen}
        options={{
          title: "More & settings",
          tabBarIcon: ({ color }) => <MaterialIcons name="more-horiz" size={40} color={color} />,
          tabBarLabel: "",
          tabBarLabelStyle: {
            textShadowColor: colorScheme === "dark" ? "white" : "black",
            textShadowOutline: 1,
            textShadowRadius: colorScheme === "dark" ? 3 : 1,
          },
        }}
      />
    </BottomTab.Navigator>
  );
}



function TabBarIcon(props) {
  return <FontAwesome size={30} style={{ marginBottom: -3, width: 50 }} {...props} />;
}
